/**
 * SystemMessages – henter aktive system-meldinger og viser dem som banner/modal.
 */
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { X, AlertTriangle, Info } from "lucide-react";
import api from "../lib/api";

export default function SystemMessages({ user }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!user || user === false) return;
    let cancelled = false;
    const fetchMsgs = async () => {
      setLoading(true);
      try {
        const r = await api.get("/me/system-messages");
        if (!cancelled) setItems(r.data?.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchMsgs();
    return () => { cancelled = true; };
  }, [user?.id, location.pathname]);

  const ack = async (id) => {
    setItems((cur) => cur.filter((m) => m.id !== id));
    try { await api.post(`/me/system-messages/${id}/ack`); } catch {}
  };

  if (!user || user === false || loading || items.length === 0) return null;

  const important = items.find((m) => m.severity === "important");
  const infoItems = items.filter((m) => m.severity !== "important");

  return (
    <>
      {infoItems.length > 0 && (
        <div className="sticky top-0 z-40" data-testid="system-info-stack">
          {infoItems.map((m) => (
            <div
              key={m.id}
              className="border-b border-[var(--magenta)]/30 bg-[var(--magenta)]/10 backdrop-blur-md"
              data-testid={`system-info-${m.id}`}
            >
              <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-start gap-3">
                <Info className="h-4 w-4 text-[var(--magenta)] mt-0.5 shrink-0" />
                <div className="flex-1 text-sm text-[var(--cream)]">
                  <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-0.5">
                    {m.title}
                  </div>
                  <div className="text-[var(--cream-soft)] whitespace-pre-line">{m.body}</div>
                </div>
                <button
                  onClick={() => ack(m.id)}
                  className="text-[var(--cream-faint)] hover:text-[var(--magenta)] shrink-0 p-1"
                  aria-label={t("system_msg.close_label")}
                  data-testid={`system-info-close-${m.id}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {important && (
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center bg-black/85 backdrop-blur-sm p-4 pt-24 pb-12 overflow-y-auto"
          data-testid={`system-important-${important.id}`}
        >
          <div className="max-w-lg w-full panel-tight p-6 border-2 border-[var(--magenta)]">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="h-6 w-6 text-[var(--magenta)] shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-1">
                  {t("system_msg.important_label")}
                </div>
                <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">
                  {important.title}
                </h2>
              </div>
            </div>
            <div className="text-[var(--cream-soft)] whitespace-pre-line mb-6 leading-relaxed">
              {important.body}
            </div>
            <button
              onClick={() => ack(important.id)}
              className="btn-magenta w-full"
              data-testid={`system-important-ack-${important.id}`}
            >
              {t("system_msg.ack_btn")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
